import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Sun, Moon, LogOut, LayoutDashboard, ShieldCheck, User as UserIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const navLinks = [
    { to: "/marketplace", label: "Marketplace" },
    { to: "/jv", label: "Joint Ventures" },
    { to: "/businesses", label: "Businesses" },
    { to: "/tourism", label: "Tourism" },
    { to: "/services", label: "Services" },
    { to: "/government-services", label: "Govt Liaison" },
    { to: "/concierge", label: "Concierge" },
    { to: "/invest", label: "Invest in Fiji" },
    { to: "/agents", label: "Agents" },
    { to: "/hall-of-fame", label: "Hall of Fame" },
    { to: "/how-it-works", label: "How It Works" },
];

export default function Navbar() {
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    return (
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border/60">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2.5 group shrink-0">
                    <div className="dark:bg-white dark:p-1 dark:rounded-lg dark:shadow-sm transition-all">
                        <img
                            src="/brand/logo_icon_512.png"
                            alt="Bula Real Estate Fiji"
                            className="h-9 w-9 object-contain transition-transform group-hover:scale-105"
                        />
                    </div>
                    <div className="leading-tight whitespace-nowrap">
                        <div className="font-display text-sm font-bold text-ocean dark:text-turquoise">
                            Bula Real Estate
                        </div>
                        <div className="text-[9px] tracking-[0.3em] uppercase text-muted-foreground font-app">
                            Fiji
                        </div>
                    </div>
                </Link>

                <nav className="hidden xl:flex items-center gap-0.5">
                    {navLinks.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `px-2.5 py-2 rounded-md font-app text-[13px] font-medium whitespace-nowrap transition-colors ${
                                    isActive
                                        ? "text-ocean dark:text-turquoise bg-secondary"
                                        : "text-foreground/80 hover:text-ocean dark:hover:text-turquoise hover:bg-secondary/60"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2 shrink-0">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={toggle}
                        data-testid="theme-toggle-btn"
                        aria-label="Toggle theme"
                    >
                        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    </Button>

                    {user ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" data-testid="user-menu-btn" className="font-app">
                                    {user.full_name?.split(" ")[0] || "Account"}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel className="font-app">
                                    <div className="font-medium">{user.full_name}</div>
                                    <div className="text-xs text-muted-foreground">{user.email}</div>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                                    <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                                    <UserIcon className="h-4 w-4 mr-2" /> Profile & lanes
                                </DropdownMenuItem>
                                {(user.roles?.includes("agent") || user.services?.includes("agent") || user.roles?.includes("admin")) && (
                                    <DropdownMenuItem onClick={() => navigate("/agent-console")} data-testid="menu-agent-console">
                                        <LayoutDashboard className="h-4 w-4 mr-2" /> Agent Console
                                    </DropdownMenuItem>
                                )}
                                {user.roles?.includes("admin") && (
                                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                                        <ShieldCheck className="h-4 w-4 mr-2" /> Admin Panel
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                                    <LogOut className="h-4 w-4 mr-2" /> Sign out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <div className="hidden sm:flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => navigate("/login")} data-testid="navbar-login-btn" className="font-app">
                                Sign in
                            </Button>
                            <Button size="sm" onClick={() => navigate("/register")} data-testid="navbar-register-btn" className="bg-ocean-green text-white font-app hover:opacity-90">
                                Get started
                            </Button>
                        </div>
                    )}

                    <Button variant="ghost" size="icon" className="xl:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </Button>
                </div>
            </div>

            {open && (
                <div className="xl:hidden border-t border-border bg-background animate-fade-in">
                    <div className="px-4 py-3 space-y-1">
                        {navLinks.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                onClick={() => setOpen(false)}
                                className={({ isActive }) =>
                                    `block px-4 py-2 rounded-md font-app text-sm ${
                                        isActive ? "bg-secondary text-ocean dark:text-turquoise" : "text-foreground/80"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        {!user && (
                            <div className="pt-2 flex gap-2">
                                <Button variant="outline" className="flex-1" onClick={() => { setOpen(false); navigate("/login"); }}>Sign in</Button>
                                <Button className="flex-1 bg-ocean-green text-white" onClick={() => { setOpen(false); navigate("/register"); }}>Get started</Button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
